import { useCallback, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import { RAILBAR_TYPES, type RailbarType } from "./railbarTypes";

interface UseMemberProfileRailbarArgs {
  setShowRailBar: Dispatch<SetStateAction<boolean>>;
  setType: Dispatch<SetStateAction<RailbarType>>;
  shouldShowRailBar: boolean;
  type: RailbarType;
}

/**
 * Owns the member profile panel of the railbar: which member it shows, and what
 * the railbar goes back to once the profile is dismissed.
 */
export const useMemberProfileRailbar = ({
  setShowRailBar,
  setType,
  shouldShowRailBar,
  type,
}: UseMemberProfileRailbarArgs) => {
  const [memberProfilePublicId, setMemberProfilePublicId] = useState<
    string | undefined
  >();
  // What the railbar was showing before the profile took it over, so dismissing
  // the profile restores that panel instead of guessing.
  const [returnState, setReturnState] = useState<{
    shouldShowRailBar: boolean;
    type: RailbarType;
  } | null>(null);

  const isMemberProfileOpen = type === RAILBAR_TYPES.MEMBER_PROFILE;

  const openMemberProfileRailbar = useCallback(
    (memberPublicId: string) => {
      if (!memberPublicId) return;
      setReturnState(previousState =>
        isMemberProfileOpen ? previousState : { shouldShowRailBar, type },
      );
      setMemberProfilePublicId(memberPublicId);
      setType(RAILBAR_TYPES.MEMBER_PROFILE);
      setShowRailBar(true);
    },
    [isMemberProfileOpen, setShowRailBar, setType, shouldShowRailBar, type],
  );

  const closeMemberProfileRailbar = useCallback(() => {
    setType(returnState?.type ?? RAILBAR_TYPES.PARTICIPANTS);
    setShowRailBar(returnState?.shouldShowRailBar ?? false);
    setMemberProfilePublicId(undefined);
    setReturnState(null);
  }, [returnState, setShowRailBar, setType]);

  /**
   * Drops the profile without touching the railbar visibility, so collapsing
   * the railbar does not bring the profile back on the next expand.
   */
  const dismissMemberProfile = useCallback(() => {
    if (!isMemberProfileOpen) return;
    setType(RAILBAR_TYPES.PARTICIPANTS);
    setMemberProfilePublicId(undefined);
    setReturnState(null);
  }, [isMemberProfileOpen, setType]);

  return {
    closeMemberProfileRailbar,
    dismissMemberProfile,
    memberProfilePublicId,
    openMemberProfileRailbar,
  };
};
